import type { MeasurementSource } from "./measurement";

/**
 * Room — a named space on a Floor, classified by a configurable Room Type.
 *
 * Rooms exist WITHOUT geometry (spec Section 16): Phase 3 measurements are
 * optional manual entries. Values are exact integers (area: sqft × 100;
 * perimeter: linear ft × 100; ceiling height: whole inches), each with a
 * provenance source ('manual' in Phase 3). When the floorplan arrives,
 * geometry-derived values get their OWN columns beside these; the
 * effective-value rule (user value wins, else geometry) lives in the
 * domain layer, and neither overwrites the other (spec Section 21).
 */
export interface Room {
  id: string;
  floorId: string;
  name: string;
  roomTypeId: string;
  /** Floor area, sqft × 100; null = not entered. */
  floorAreaX100: number | null;
  floorAreaSource: MeasurementSource | null;
  /** Perimeter, linear ft × 100; null = not entered. */
  perimeterX100: number | null;
  perimeterSource: MeasurementSource | null;
  /** Ceiling height in whole inches; null = not entered. */
  ceilingHeightInches: number | null;
  /**
   * Spec §89: the tape-measure size of a rectangular room, feet × 100; null =
   * not entered. Kept beside the area and perimeter it produced so the form
   * can show "12' × 15'" again and the estimate can say where its area came
   * from. Area and perimeter stay the values everything else reads.
   */
  lengthX100: number | null;
  widthX100: number | null;
  notes: string;
  sortOrder: number;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Geometry-derived room values (Phase 7), cached from the floorplan by the
 * DesignScopeCoordinator. Kept in room_geometry, never written into the
 * manual columns. `status`: 'bound' (anchor inside one region), 'unbound'
 * (anchor outside every region → no geometry), 'conflict' (two rooms share
 * a region → values withheld until resolved).
 */
export interface RoomGeometry {
  /** The Design (scenario) this binding belongs to. */
  designId: string;
  roomId: string;
  floorId: string;
  anchorX: number;
  anchorY: number;
  floorAreaX100: number | null;
  perimeterX100: number | null;
  status: "bound" | "unbound" | "conflict";
  updatedAt: string;
}

export interface EffectiveMeasurement {
  /** The value the rest of the app uses (null = nothing known). */
  valueX100: number | null;
  /** Where it came from. */
  source: "manual" | "geometry" | "override" | null;
  /** Both underlying values, for display (nothing is destroyed). */
  manualX100: number | null;
  geometryX100: number | null;
  /** Geometry exists, a legacy manual value exists, and the user has not yet chosen. */
  needsReconciliation: boolean;
}

/**
 * Approved decision 5: geometry is the calculated value when valid geometry
 * exists, unless the user explicitly keeps a manual OVERRIDE. A legacy
 * 'manual' value next to new geometry yields geometry + a one-time
 * reconciliation prompt; choosing "Use Geometry" marks the source
 * 'geometry', choosing "Keep Manual Override" marks it 'override'.
 */
export function effectiveMeasurement(
  manualX100: number | null,
  manualSource: MeasurementSource | null,
  geometryX100: number | null,
): EffectiveMeasurement {
  const hasGeometry = geometryX100 !== null;
  if (manualSource === "override" && manualX100 !== null) {
    return { valueX100: manualX100, source: "override", manualX100, geometryX100, needsReconciliation: false };
  }
  if (hasGeometry) {
    return {
      valueX100: geometryX100,
      source: "geometry",
      manualX100,
      geometryX100,
      needsReconciliation: manualX100 !== null && manualSource === "manual",
    };
  }
  if (manualX100 !== null) {
    return { valueX100: manualX100, source: manualSource === "override" ? "override" : "manual", manualX100, geometryX100, needsReconciliation: false };
  }
  return { valueX100: null, source: null, manualX100, geometryX100, needsReconciliation: false };
}

export function effectiveFloorArea(room: Room, geometry: RoomGeometry | null): EffectiveMeasurement {
  return effectiveMeasurement(room.floorAreaX100, room.floorAreaSource, geometry && geometry.status === "bound" ? geometry.floorAreaX100 : null);
}

export function effectivePerimeter(room: Room, geometry: RoomGeometry | null): EffectiveMeasurement {
  return effectiveMeasurement(room.perimeterX100, room.perimeterSource, geometry && geometry.status === "bound" ? geometry.perimeterX100 : null);
}

export interface RoomInput {
  name: string;
  roomTypeId: string;
  floorAreaX100: number | null;
  perimeterX100: number | null;
  ceilingHeightInches: number | null;
  /** Spec §89: rectangular dimensions, feet × 100 (optional; omitted = unchanged meaning "not entered"). */
  lengthX100?: number | null;
  widthX100?: number | null;
  notes: string;
}

/* ------------------------------------------------------------------------
 * ROOM QUANTITIES WITHOUT A DRAWING (spec §89).
 *
 * ONE set of formulas for every screen, the package planner, the scope
 * coordinator and the §82 finish engine. A room walked with a tape measure
 * and a room drawn in Design feed the same numbers into the same estimate.
 * ---------------------------------------------------------------------- */

/** The wall height assumed when neither the room nor its floor states one (8 ft). */
export const DEFAULT_WALL_HEIGHT_INCHES = 96;

/** Floor area of a rectangle, sqft × 100, from feet × 100 sides (half-up). */
export function rectangleAreaX100(lengthX100: number, widthX100: number): number {
  return Math.round((lengthX100 * widthX100) / 100);
}

/** Perimeter of a rectangle, linear ft × 100. */
export function rectanglePerimeterX100(lengthX100: number, widthX100: number): number {
  return 2 * (lengthX100 + widthX100);
}

/**
 * GROSS wall surface, sqft × 100: perimeter × wall height. Doors and windows
 * are not deducted — the painting trade's own convention (§82-15), and the
 * honest reading of what a room record holds.
 */
export function grossWallAreaX100(perimeterX100: number, wallHeightInches: number): number {
  return Math.round((perimeterX100 * wallHeightInches) / 12);
}

export interface EffectiveWallHeight {
  inches: number;
  /** 'room' — typed for this room; 'floor' — the floor's default; 'assumed' — 8 ft. */
  source: "room" | "floor" | "assumed";
}

export function effectiveWallHeight(room: Pick<Room, "ceilingHeightInches">, floorDefaultInches: number | null): EffectiveWallHeight {
  if (room.ceilingHeightInches !== null && room.ceilingHeightInches > 0) return { inches: room.ceilingHeightInches, source: "room" };
  if (floorDefaultInches !== null && floorDefaultInches > 0) return { inches: floorDefaultInches, source: "floor" };
  return { inches: DEFAULT_WALL_HEIGHT_INCHES, source: "assumed" };
}

/**
 * Wall surface from the room's EFFECTIVE perimeter (drawn, kept override or
 * typed — the same rule every other room quantity follows) and its wall
 * height. Null when no perimeter is known; a height is never a reason to
 * withhold it, because the assumption is stated.
 */
export function effectiveWallArea(
  room: Room,
  geometry: RoomGeometry | null,
  floorDefaultInches: number | null,
): { valueX100: number | null; perimeter: EffectiveMeasurement; height: EffectiveWallHeight } {
  const perimeter = effectivePerimeter(room, geometry);
  const height = effectiveWallHeight(room, floorDefaultInches);
  return { valueX100: perimeter.valueX100 === null ? null : grossWallAreaX100(perimeter.valueX100, height.inches), perimeter, height };
}

/**
 * True when the room's typed area and perimeter are exactly what its typed
 * length × width produce — the only case where the estimate may say
 * "from room dimensions" rather than "typed area".
 */
export function measuredFromDimensions(room: Pick<Room, "lengthX100" | "widthX100" | "floorAreaX100" | "perimeterX100">): boolean {
  if (room.lengthX100 === null || room.widthX100 === null) return false;
  return (
    room.floorAreaX100 === rectangleAreaX100(room.lengthX100, room.widthX100) &&
    room.perimeterX100 === rectanglePerimeterX100(room.lengthX100, room.widthX100)
  );
}

export function validateRoomInput(input: RoomInput): string[] {
  const problems: string[] = [];
  if (input.name.trim() === "") {
    problems.push("Room name is required.");
  }
  if (input.roomTypeId.trim() === "") {
    problems.push("Room type is required.");
  }
  for (const [label, value] of [
    ["Floor area", input.floorAreaX100],
    ["Perimeter", input.perimeterX100],
    ["Ceiling height", input.ceilingHeightInches],
    ["Length", input.lengthX100 ?? null],
    ["Width", input.widthX100 ?? null],
  ] as const) {
    if (value !== null && (!Number.isSafeInteger(value) || value < 0)) {
      problems.push(`${label} must be a valid non-negative number.`);
    }
  }
  return problems;
}
